import moment from 'moment-timezone';
import logger from '../../../utils/logger';
import { toNumber } from '../../../utils/numbers';
import convertUnit from '../../../utils/unit-converter';
import { WeighedDataToBatch } from '../../../containers/Batch/DataToBatch';
import { Material } from '../../../../types/api/Material';
import { Device } from '../../../../types/api/Device';
import {
  canBatchInProportions,
  getRemainingSchedule,
} from './proportion-schedule';

// CONFIGURATION
const CHANGE_MATERIAL_MID_BATCH_CONFIG = {
  updateInventory: false,
};

export const updateInventoryIfEnabled = (
  materialStore: any,
  batchStore: any,
  originalMaterial: Material,
  substituteMaterial: Material,
  currentWeight: number,
  scale: Device,
  runningData: any,
  originalBins: Device[],
) => {
  if (currentWeight > 0 && batchStore.canReduceInventory && CHANGE_MATERIAL_MID_BATCH_CONFIG.updateInventory) {
    const originalQuantity = toNumber(convertUnit(currentWeight, batchStore.getUnitById(scale.unitId), originalMaterial.itemInventoryUnitSymbol || ''), 4);

    materialStore.updateMaterialInventoryUse(originalMaterial, {
      quantity: originalQuantity,
      comment: `Substituted with ${substituteMaterial.code}`,
      ticket: runningData.tId,
      isAutomatic: false,
      binIds: originalBins.map(bin => bin.id),
    });

    logger.log('Updated inventory for original material:', originalMaterial.code, originalQuantity);
  }
};

export const createSubstituteWeighedData = (
  scaleData: any,
  runningData: any,
  substituteMaterial: Material,
  substituteMaterialDetails: Material,
  substituteBin: Device,
  originalMaterial: Material,
  originalBin: Device,
  reason: string,
  currentWeight: number,
  currentScaleWeight: number,
  batchStore: any,
): WeighedDataToBatch => {
  // Swapped during proportion one: feed the rest of it now and keep proportion two for later, in the original's slots.
  // batchMaterial only runs proportions for some material types; any other substitute feeds everything in one go.
  const schedule = getRemainingSchedule(scaleData, currentWeight);
  const keepsSchedule = schedule.later > 0 && canBatchInProportions(batchStore.materialStore, substituteMaterial.itemId);
  const remainingTarget = keepsSchedule ? schedule.now : schedule.total;
  const remainingTargetInMaterialUnits = toNumber(convertUnit(
    remainingTarget,
    batchStore.getUnitById(scaleData.scale.unitId),
    substituteMaterial.itemBatchUnitSymbol || '',
  ), 5);

  const constituent = scaleData.constituent || runningData.mixDesign?.find((c: any) =>
    c.constituentItemId === originalMaterial.itemId ||
    (c.batchCode && originalMaterial.batchCode && c.batchCode === originalMaterial.batchCode),
  );

  return new WeighedDataToBatch({
    constituent: constituent ? {
      ...constituent,
      constituentItemId: substituteMaterial.itemId,
      constituentItemCode: substituteMaterial.code,
      batchCode: substituteMaterial.batchCode,
      batchShortDescription: substituteMaterial.batchShortDescription,
      binPercent: {},
    } : null,
    material: substituteMaterial,
    materialDetails: substituteMaterialDetails,
    binPercent: {},
    bin: substituteBin,
    binForDecum: scaleData.binForDecum,
    scale: scaleData.scale,
    mft: remainingTarget,
    unconvertedTarget: remainingTargetInMaterialUnits,
    ...(keepsSchedule ? {
      proportionOneTarget: schedule.now,
      proportionTwoTarget: schedule.later,
      batchOrderOverride: schedule.batchOrder,
      proportionTwoOrderOverride: schedule.proportionTwoOrder,
    } : {}),
    jogSecondary: false,
    jogCounter: scaleData.jogCounter || 0,
    originalMaterial: originalMaterial,
    originalBin: originalBin,
    substitutionReason: reason,
    substitutionWeight: currentWeight,
    substitutionTimestamp: moment().tz(batchStore.timezone).format(),
    isSubstituted: true,
    isFeedInterrupted: true,
    isFeedFinish: false,
    startWeight: currentScaleWeight,
    feedLogs: [],
  });
};
